import type { ConversationListItem } from '@kimuno/api-sdk';

import { conversationListSettings } from '../../../../constants';
import type { Pagination } from '../../../../types/AppContent';
export interface GroupedItems {
	[key: string]: ConversationListItem[];
}

export interface TimeGroupConfig {
	title: string;
	timeframeInSeconds: number;
	priority: number; // Lower number = higher priority in sorting
}

export interface MonthYearGroupConfig {
	monthNames: string[];
	useCurrentYearMonths: boolean;
	useYearGroups: boolean;
}

export interface ChatHistoryGroupingConfig {
	timeGroups: TimeGroupConfig[];
	monthYearConfig: MonthYearGroupConfig;
}

// Default configuration
export const DEFAULT_CHAT_HISTORY_GROUPING_CONFIG: ChatHistoryGroupingConfig = {
	timeGroups: [
		{
			title: 'Today',
			timeframeInSeconds: 0, // Special case for "today"
			priority: 1,
		},
		{
			title: 'Last 7 days',
			timeframeInSeconds: 60 * 60 * 24 * 7, // 7 days
			priority: 2,
		},
		{
			title: 'Last 30 days',
			timeframeInSeconds: 60 * 60 * 24 * 30, // 30 days
			priority: 3,
		},
	],
	monthYearConfig: {
		monthNames: [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December',
		],
		useCurrentYearMonths: true,
		useYearGroups: true,
	},
};

/**
 * Groups chat history items by configurable time frames
 */
export const groupItemsByTimeFrame = (
	items: ConversationListItem[],
	config: ChatHistoryGroupingConfig = DEFAULT_CHAT_HISTORY_GROUPING_CONFIG,
	currentDate: Date = new Date()
): GroupedItems => {
	const now = currentDate;
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const groups: GroupedItems = {};

	// Sort time groups by priority for processing order
	const sortedTimeGroups = [...config.timeGroups].sort((a, b) => a.priority - b.priority);

	items.forEach((item) => {
		const itemDate = new Date(item.updatedAt);
		const itemDay = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
		let grouped = false;

		// Check against configured time groups
		for (const timeGroup of sortedTimeGroups) {
			if (timeGroup.timeframeInSeconds === 0) {
				// Special case for "today"
				if (itemDay.getTime() === today.getTime()) {
					if (!groups[timeGroup.title]) groups[timeGroup.title] = [];
					groups[timeGroup.title].push(item);
					grouped = true;
					break;
				}
			} else {
				// Calculate the cutoff date for this time group
				const cutoffDate = new Date(today.getTime() - timeGroup.timeframeInSeconds * 1000);

				if (itemDay >= cutoffDate) {
					// Check if it's not already grouped in a higher priority group
					let alreadyGrouped = false;
					for (const higherPriorityGroup of sortedTimeGroups) {
						if (higherPriorityGroup.priority >= timeGroup.priority) break;

						if (higherPriorityGroup.timeframeInSeconds === 0) {
							// Check if it was already grouped as "today"
							if (itemDay.getTime() === today.getTime()) {
								alreadyGrouped = true;
								break;
							}
						} else {
							const higherCutoff = new Date(
								today.getTime() - higherPriorityGroup.timeframeInSeconds * 1000
							);
							if (itemDay >= higherCutoff) {
								alreadyGrouped = true;
								break;
							}
						}
					}

					if (!alreadyGrouped) {
						if (!groups[timeGroup.title]) groups[timeGroup.title] = [];
						groups[timeGroup.title].push(item);
						grouped = true;
						break;
					}
				}
			}
		}

		// If not grouped by time frames, group by month/year
		if (!grouped) {
			const currentYear = now.getFullYear();
			const itemYear = itemDate.getFullYear();

			if (config.monthYearConfig.useCurrentYearMonths && itemYear === currentYear) {
				// Same year - group by month
				const monthKey = config.monthYearConfig.monthNames[itemDate.getMonth()];
				if (!groups[monthKey]) groups[monthKey] = [];
				groups[monthKey].push(item);
			} else if (config.monthYearConfig.useYearGroups) {
				// Different year - group by year
				const yearKey = itemYear.toString();
				if (!groups[yearKey]) groups[yearKey] = [];
				groups[yearKey].push(item);
			}
		}
	});

	// Sort items within each group by updatedAt (newest first)
	Object.keys(groups).forEach((key) => {
		groups[key].sort((a, b) => b.updatedAt - a.updatedAt);
	});

	return groups;
};

/**
 * Sorts group keys in chronological order based on configuration
 */
export const sortGroupKeys = (
	keys: string[],
	config: ChatHistoryGroupingConfig = DEFAULT_CHAT_HISTORY_GROUPING_CONFIG
): string[] => {
	return keys.sort((a, b) => {
		// First, check if they are configured time groups
		const aTimeGroup = config.timeGroups.find((group) => group.title === a);
		const bTimeGroup = config.timeGroups.find((group) => group.title === b);

		if (aTimeGroup && bTimeGroup) {
			return aTimeGroup.priority - bTimeGroup.priority;
		}

		if (aTimeGroup && !bTimeGroup) return -1; // Time groups come first
		if (!aTimeGroup && bTimeGroup) return 1; // Time groups come first

		// Check if they are months
		const aIsMonth = config.monthYearConfig.monthNames.includes(a);
		const bIsMonth = config.monthYearConfig.monthNames.includes(b);

		if (aIsMonth && bIsMonth) {
			// Both are months - sort by month order (newest first)
			return (
				config.monthYearConfig.monthNames.indexOf(b) - config.monthYearConfig.monthNames.indexOf(a)
			);
		}

		if (aIsMonth && !bIsMonth) return -1; // Months come before years
		if (!aIsMonth && bIsMonth) return 1; // Years come after months

		// Both are years - sort numerically (newest first)
		return Number.parseInt(b) - Number.parseInt(a);
	});
};

export function hasNextConversationListPage(pagination: Pagination) {
	return (
		pagination.offset <= conversationListSettings.maxOffset &&
		((pagination.total === -1 && pagination.offset === 0) || pagination.total > pagination.offset)
	);
}
